import { useState } from 'react';
import styled from 'styled-components';
import { DestructiveButton } from 'ui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'ui/modal';
import { Body2, BoldText } from 'ui/theme';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { ARCHIVE_TEXT } from '../constants';

const ListItem = styled.li`
  list-style-type: disc;
`;

type ArchiveModalProps = {
  /** Bool to show or hide the modal */
  open: boolean;
  /** Function that closes the modal */
  closeModal: () => void;
  /** Function to call when submit is clicked */
  handleAction: (project: Record<string, any>) => void;
  /** Object that holds the project data */
  project: Record<string, any>;
  /** Name of the organization of the project */
  organizationName: string;
};

export const ArchiveModal = ({
  closeModal,
  handleAction,
  open = false,
  project = {},
  organizationName,
}: ArchiveModalProps) => {
  const [archiveText, setArchiveText] = useState('');

  const mayArchive = archiveText === ARCHIVE_TEXT;

  const handleClose = () => {
    setArchiveText('');
    closeModal();
  };

  return open ? (
    <Modal onClose={handleClose} isModalOpen={open} data-test="ArchiveModal" id="ArchiveModal">
      <ModalHeader>Archive project</ModalHeader>
      <ModalBody>
        <Body2>
          Are you sure you‘d like to archive <BoldText>{project.name}</BoldText> (id {project.id})
          in org {organizationName}?
        </Body2>
        <ul>
          <ListItem>All campaigns and workflows will be turned off.</ListItem>
          <ListItem>All API keys will be disabled.</ListItem>
          <ListItem>Members will lose access to the project and its contents.</ListItem>
        </ul>
        <FormField
          inputId="projects-archive-confirmation"
          labelText={`Type ${ARCHIVE_TEXT} to proceed`}
        >
          <InputText
            data-test="projects-archive-confirmation"
            id="projects-archive-confirmation"
            onChange={(value) => setArchiveText(value)}
            value={archiveText}
          />
        </FormField>
      </ModalBody>
      <ModalFooter hasCancel onClose={closeModal}>
        <DestructiveButton
          isDisabled={!mayArchive}
          data-test="ArchiveModal-Submit"
          onClick={() => {
            handleAction({
              ...project,
              extendedProjectSettings: { ...project.extendedProjectSettings, isArchived: true },
            });
            handleClose();
          }}
        >
          Archive
        </DestructiveButton>
      </ModalFooter>
    </Modal>
  ) : null;
};
